/**
 * Calculadora de riesgo sobre la foto de Syntage (plan v4.2 §6.3, §6.4 y §8b.1). Espejo del contrato de
 * `GET /clients/:id/risk-scorecard` y `GET /leases/:id/risk-scorecard` de `api-rendafin`: el panel pinta lo que
 * calcula el motor y no recalcula nada (ADR-0002). Las pestañas Flujos, Estados financieros, Ratios y Calculadora
 * leen esta misma respuesta.
 */

export type RiskBand = 'favorable' | 'review' | 'unfavorable'
//* `no_aplica`: métrica opcional sin dato (H5): vale el neutro y no cuenta en la cobertura (motor 2.0).
export type MetricState = 'real' | 'parcial' | 'sin_dato' | 'no_aplica'
export type MetricUnit = 'x' | '%' | 'days' | 'mxn' | 'years' | 'hhi' | 'count' | 'pts' | 'ratio'
export type RentSource = 'conservative_offer' | 'quote' | 'conservative_offer_no_quote' | 'none'
export type PillarKey = 'character' | 'capacity' | 'capital'

export interface MetricResult {
  key: string
  label: string
  value: number | null
  unit: MetricUnit
  //* NIVEL del tramo (20 alto … 0 bajo). Lo que vale en esta métrica está en `tierPoints` (motor 2.0; un sello del motor 1 no lo trae).
  tier: 0 | 10 | 15 | 20
  tierPoints?: number
  points: number
  effectiveWeight: number
  state: MetricState
  flags: string[]
  source: { section: string | null; revision?: number | null; window?: { from: string; to: string } | null }
}

export interface PillarResult {
  key: PillarKey
  label: string
  weight: number
  //* Lo más que da el pilar en la escala del Excel (motor 2.0). En un sello del motor 1, el máximo es `weight`.
  maxPoints?: number
  score: number
  coverageBp: number
  metrics: MetricResult[]
}

export interface VetoResult {
  code: string
  label: string
  band: RiskBand
  pld: boolean
  source: { section: string; revision?: number | null }
}

export interface FxConversion {
  currency: string
  rate: number
  source: 'buro' | 'banxico_fix'
  date: string | null
  accounts: number
}

export interface StatementRow {
  key: string
  label: string
  level: number
  values: Record<string, number | null>
  verticalPct: Record<string, number | null>
  yoyPct: Record<string, number | null>
  children?: StatementRow[]
}

export interface StatementsBlock {
  source: { format: '2022' }
  years: string[]
  effectiveYear: string | null
  flags: Record<string, string[]>
  income: StatementRow[]
  balance: StatementRow[]
  check: Record<string, { assets: number | null; liabilitiesPlusEquity: number | null; ok: boolean }>
}

export interface RatioRow {
  key: string
  label: string
  formula: string
  unit: 'x' | '%' | 'days' | 'mxn'
  values: Record<string, number | null>
  //* Solo en «Flujo neto CFDI / renta»: el valor de los últimos 12 meses, el que usa la calculadora.
  trailing12m?: number | null
  usedByScorecard: boolean
  flags: string[]
}

/** Ratios de Syntage por grupo › ratio › año, como los entrega `/insights/financial-ratios`. */
export type SyntageRatios = Record<string, Record<string, Record<string, number | null>>>

export interface RatiosBlock {
  years: string[]
  groups: Array<{ key: string; label: string; ratios: RatioRow[] }>
  syntage: SyntageRatios | null
  trailingWindow: { from: string; to: string } | null
}

export interface FlowsMonth {
  month: string
  inflow: number
  outflow: number
  net: number
  salesRevenue: number | null
  expenditures: number | null
}

export interface FlowsBlock {
  source: 'cfdi'
  window: { from: string; to: string } | null
  monthly: FlowsMonth[]
  averages: Record<'m3' | 'm6' | 'm12', { inflow: number; outflow: number; net: number } | null>
  rentCoverage: { monthlyRent: number; rentSource: RentSource; netToRent12m: number | null } | null
}

export type LegalGateSignalCode = 'defendant_lawsuit' | 'criminal_record_defendant' | 'media_alert' | 'own_69b_presumed' | 'pep_found' | 'international_found' | 'tax_compliance_negative'

/** Señales que mandan una preoferta al gate de Legal (plan v4.3 D-57). `determined: false`: antecedentes o PLD sin consultar. */
export interface LegalGate {
  determined: boolean
  signals: Array<{ code: LegalGateSignalCode; kind: 'lawsuit' | 'blacklist' | 'tax_compliance'; label: string; count: number; source: { section: string; revision?: number | null } }>
}

export interface CollateralResult {
  points: number
  items: Array<{ code: 'second_guarantor' | 'solid_guarantee'; points: number; source: 'client_contacts' | 'capture'; names?: string[] }>
}

/**
 * Motor 2.0 (plan v4.3): escala del Excel — base hasta `baseMax` (90) + colateral hasta `collateralMax` (20) — y la banda
 * sobre el total. Un sello del motor 1 no trae `scale` y sí `score` (0-100), `excelEquivalent` y el bono: se pinta
 * con la escala de entonces (`isLegacyScorecard`).
 */
export interface ScorecardResult {
  engineVersion: string
  personType: 'PM' | 'PF'
  band: RiskBand | null
  //* `note`: qué hace falta para resolver ese motivo (nota de por qué se manda a revisión). Sin nota en `score`.
  bandReasons: Array<{ kind: 'score' | 'veto' | 'coverage'; code: string; label: string; note?: string }>
  scale?: { baseMax: number; collateralMax: number; totalMax: number; favorableMin: number; reviewMin: number }
  baseScore?: number | null
  collateral?: CollateralResult
  totalScore?: number | null
  legalGate?: LegalGate
  //* Solo en sellos del motor 1.
  score?: number | null
  collateralBonus?: number
  scoreWithBonus?: number | null
  excelEquivalent?: number | null
  coverageBp: number
  pillars: PillarResult[]
  vetoes: VetoResult[]
  flags: string[]
  topLosses: Array<{ metric: string; label: string; lostPoints: number }>
  creditHistory: {
    source: 'circulo' | 'syntage_buro'
    queryId: string | null
    queriedAt: string
    stale: boolean
    conversions: FxConversion[]
    unconverted: Array<{ currency: string; accounts: number }>
  } | null
  statements: StatementsBlock | null
  ratiosByYear: RatiosBlock | null
  flows: FlowsBlock | null
}

/** Respuesta de `GET …/risk-scorecard`. `result: null` cuando no hay foto que calcular. */
export interface RiskScorecardView {
  snapshot: {
    state: 'ok' | 'pre_snapshot' | 'no_verification'
    id: string | null
    verificationId: string | null
    viewedRevision: number | null
    reconstructed: boolean
    pinned: boolean
    pendingCount: number
    noEntity: boolean
    verificationProvider: 'syntage' | 'ekatena' | null
  } | null
  seal: { sealed: boolean; sealKind: 'activation' | 'retroactive' | null; activatedAt: string | null }
  config: { id: string | null; version: number; hash: string; isSystemDefault: boolean } | null
  rent: { monthly: number; source: RentSource }
  creditHistory: { queryId: string | null; source: string; queriedAt: string; stale: boolean } | null
  flags: string[]
  result: ScorecardResult | null
}

/** Captura del analista de un arrendamiento (C5 y Colateral, D-21 y D-38). */
export interface RiskCapture {
  c5: { clientCv: number; guarantorCv: number; reputation: number } | null
  //* `secondGuarantor` es de capturas viejas: el motor 2.0 detecta al 2º obligado en los contactos del cliente.
  collateral: { secondGuarantor?: boolean; solidGuarantee: boolean; notes: string | null } | null
  by: { id: string; name: string }
  at: string
  reason: string
}

export interface RiskCaptureHistory {
  current: RiskCapture | null
  history: RiskCapture[]
}

export interface SaveRiskCapture {
  c5: { clientCv: number; guarantorCv: number; reputation: number } | null
  collateral: { solidGuarantee: boolean; notes?: string } | null
  reason: string
}

/** Un resultado sellado con el motor 1 (escala 0-100): no trae `scale`. */
export function isLegacyScorecard(result: ScorecardResult | null | undefined): boolean {
  return !!result && !result.scale
}
